import { forwardRef, useEffect, useImperativeHandle } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { format, differenceInDays } from "date-fns";
import { useAPIGetReportCustomerFreeProduct } from "../api/api-get-report-customer-free-product";
import ReportCustomerFreeProductFilter from "./ReportCustomerFreeProductFilter";

const ReportCustomerFreeProductList = forwardRef((props, ref) => {
  const {
    loading,
    listData,
    fetchListData,
    filterValue,
    setFilterValue,
    getFilter,
    handleOnPage,
    paging,
    handleOnUpdateViewRow,
    handleOnDeleteViewRow,
  } = useAPIGetReportCustomerFreeProduct();

  useImperativeHandle(ref, () => ({
    handleOnUpdateViewRow: (row) => handleOnUpdateViewRow(row),
    handleOnDeleteViewRow: (row) => handleOnDeleteViewRow(row),
  }));

  useEffect(() => {
    fetchListData({ page: paging.page, take: paging.take });
  }, []);

  // Body templates

  const customerBodyTemplate = (row) => {
    const { data_customer } = row;
    return (
      <div className="col-start-start gap-1">
        <span className="font-semibold text-slate-700">{data_customer?.name}</span>
        <span className="text-xs text-slate-500">{data_customer?.code}</span>
        <span className="text-xs font-semibold text-blue-600">
          BC: {data_customer?.data_bc?.name ?? "-"}
        </span>
      </div>
    );
  };

  const productBodyTemplate = (row) => {
    const { data_product } = row;
    const used = row.usage_sales_item_id !== null && row.usage_sales_item_id !== undefined;
    return (
      <div className="col-start-start gap-1 w-full">
        <div className="row-between-center gap-2 w-full">
          <span className="font-semibold text-slate-700 text-sm">
            {data_product?.name ?? "-"}
          </span>
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-bold ${used
              ? "bg-green-100 text-green-700 border border-green-300"
              : "bg-slate-100 text-slate-500 border border-slate-300"
              }`}
          >
            {used ? "Used" : "Not Used"}
          </span>
        </div>
        {data_product?.code && (
          <span className="text-xs text-slate-500">{data_product.code}</span>
        )}
        {row.quantity > 1 && (
          <span className="text-xs text-slate-400">Qty: {row.quantity}</span>
        )}
      </div>
    );
  };

  const expiredAndUsedBodyTemplate = (row) => {
    const { expired_date } = row;
    const used = row.usage_sales_item_id !== null && row.usage_sales_item_id !== undefined;
    const txDate = row.data_usage_sales_item?.data_sales?.transaction_date;
    if (!expired_date) return "-";
    const today = new Date();
    const expDate = new Date(expired_date);
    const daysLeft = differenceInDays(expDate, today);
    const isExpired = daysLeft < 0;
    const isNear = daysLeft >= 0 && daysLeft <= 30;
    const suppressExpiredStyle = used && isExpired;
    return (
      <div className="col-start-start gap-1">
        <span
          className={`text-sm font-semibold ${suppressExpiredStyle
            ? "text-slate-700"
            : isExpired
              ? "text-red-600"
              : isNear
                ? "text-orange-500"
                : "text-slate-700"
            }`}
        >
          {format(expDate, "dd MMM yyyy")}
        </span>
        {!used && (
          <span
            className={`text-xs ${isExpired
              ? "text-red-500"
              : isNear
                ? "text-orange-400"
                : "text-slate-400"
              }`}
          >
            {isExpired
              ? `Expired ${Math.abs(daysLeft)} days ago`
              : daysLeft === 0
                ? "Expires today!"
                : `${daysLeft} days left`}
          </span>
        )}
        {txDate && (
          <span className="text-xs text-slate-400">
            Used: {format(new Date(txDate), "dd MMM yyyy HH:mm")}
          </span>
        )}
      </div>
    );
  };



  return (
    <div className="col-start-start w-full gap-4">
      {/* filter section */}
      <div className="elevated-card-tight-vertical row-start-start w-full gap-4">
        <ReportCustomerFreeProductFilter
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          onClickRefresh={() => {
            const filter = getFilter(filterValue);
            fetchListData({ page: 1, take: paging.take, filter });
          }}
        />
      </div>

      {/* table section */}
      <div className="elevated-card-tight-vertical col-start-center w-full gap-2">
        <DataTable
          dataKey="id"
          className="w-100"
          loading={loading}
          showGridlines
          stripedRows
          value={listData}
        >
          <Column
            field="no"
            header="#"
            className="text-center"
            headerClassName="w-[4rem]"
            alignHeader="center"
            bodyStyle={{ textAlign: "center" }}
          />
          <Column
            field="data_customer.name"
            header="Customer"
            body={customerBodyTemplate}
          />
          <Column
            field="data_product.name"
            header="Free Product"
            body={productBodyTemplate}
          />
          <Column
            field="expired_date"
            header="Expired & Used"
            body={expiredAndUsedBodyTemplate}
          />
        </DataTable>
        <Paginator
          first={parseInt(paging.take) * (paging.page - 1)}
          rows={parseInt(paging.take)}
          totalRecords={parseInt(paging.total)}
          rowsPerPageOptions={[5, 10, 20, 50]}
          onPageChange={handleOnPage}
        />
      </div>
    </div>
  );
});

export default ReportCustomerFreeProductList;
